import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './ExceptionalAppointmentsCalendar.css';

const ExceptionalAppointmentsCalendar = ({ appointments, onChange }) => {
  const { t, i18n } = useTranslation();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [dayTimeSlots, setDayTimeSlots] = useState({}); // { '2024-01-15': [{ start: '09:00', end: '10:00', reason: '' }] }
  const [globalReason, setGlobalReason] = useState('');
  const isInitialized = useRef(false);
  const isInitializing = useRef(false);

  // Initialize from existing appointments (only once)
  useEffect(() => {
    if (!isInitialized.current && appointments) {
      isInitializing.current = true;
      const daysSet = new Set();
      const slotsMap = {};
      
      if (appointments.length > 0) {
        appointments.forEach(apt => {
          if (apt.date && apt.date !== '') {
            daysSet.add(apt.date);
            if (!slotsMap[apt.date]) {
              slotsMap[apt.date] = [];
            }
            slotsMap[apt.date].push({
              start: apt.start || '',
              end: apt.end || '',
              reason: apt.reason || ''
            });
          }
        });
      }
      
      setSelectedDays(daysSet);
      setDayTimeSlots(slotsMap);
      isInitialized.current = true;
      isInitializing.current = false;
    }
  }, [appointments]);

  // Update parent component when appointments change (but not during initialization)
  useEffect(() => {
    if (isInitializing.current || !isInitialized.current) {
      return;
    }

    const appointmentsArray = [];
    selectedDays.forEach(date => {
      if (dayTimeSlots[date] && dayTimeSlots[date].length > 0) {
        dayTimeSlots[date].forEach(slot => {
          if (slot.start && slot.end) {
            appointmentsArray.push({
              id: appointmentsArray.length + 1,
              date: date,
              start: slot.start,
              end: slot.end,
              reason: slot.reason || globalReason
            });
          }
        });
      }
    });
    
    if (onChange) {
      onChange(appointmentsArray);
    }
  }, [selectedDays, dayTimeSlots, globalReason, onChange]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const formatDateKey = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toggleDaySelection = (date) => {
    if (!date) return;
    const dateKey = formatDateKey(date);
    const newSelectedDays = new Set(selectedDays);
    
    if (newSelectedDays.has(dateKey)) {
      newSelectedDays.delete(dateKey);
      const newSlots = { ...dayTimeSlots };
      delete newSlots[dateKey];
      setDayTimeSlots(newSlots);
    } else {
      newSelectedDays.add(dateKey);
      if (!dayTimeSlots[dateKey]) {
        setDayTimeSlots({
          ...dayTimeSlots,
          [dateKey]: [{ start: '', end: '', reason: '' }]
        });
      }
    }
    
    setSelectedDays(newSelectedDays);
  };

  const addTimeSlot = (dateKey) => {
    setDayTimeSlots({
      ...dayTimeSlots,
      [dateKey]: [...(dayTimeSlots[dateKey] || []), { start: '', end: '', reason: '' }]
    });
  };

  const removeTimeSlot = (dateKey, index) => {
    const slots = dayTimeSlots[dateKey] || [];
    if (slots.length > 1) {
      const newSlots = slots.filter((_, i) => i !== index);
      setDayTimeSlots({
        ...dayTimeSlots,
        [dateKey]: newSlots
      });
    } else {
      // Remove the day if no time slots left
      const newSelectedDays = new Set(selectedDays);
      newSelectedDays.delete(dateKey);
      setSelectedDays(newSelectedDays);
      const newSlots = { ...dayTimeSlots };
      delete newSlots[dateKey];
      setDayTimeSlots(newSlots);
    }
  };

  const updateTimeSlot = (dateKey, index, field, value) => {
    const slots = [...(dayTimeSlots[dateKey] || [])];
    slots[index] = { ...slots[index], [field]: value };
    setDayTimeSlots({
      ...dayTimeSlots,
      [dateKey]: slots
    });
  };

  const applyReasonToAll = () => {
    if (!globalReason) return;
    const newSlots = { ...dayTimeSlots };
    Object.keys(newSlots).forEach(dateKey => {
      newSlots[dateKey] = newSlots[dateKey].map(slot => ({
        ...slot,
        reason: globalReason
      }));
    });
    setDayTimeSlots(newSlots);
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const monthNames = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  const weekDays = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

  const days = getDaysInMonth(currentMonth);
  const today = new Date();
  const todayKey = formatDateKey(today);

  return (
    <div className="exceptional-calendar-container">
      <div className="calendar-header">
        <div className="calendar-navigation">
          <button className="nav-btn" onClick={goToPreviousMonth}>
            <i className="fas fa-chevron-right"></i>
          </button>
          <h3 className="calendar-month-title">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <button className="nav-btn" onClick={goToNextMonth}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <button className="today-btn" onClick={goToToday}>
            اليوم
          </button>
        </div>
      </div>

      <div className="calendar-grid">
        <div className="calendar-weekdays">
          {weekDays.map(day => (
            <div key={day} className="weekday-header">
              {day}
            </div>
          ))}
        </div>
        <div className="calendar-days">
          {days.map((date, index) => {
            const dateKey = date ? formatDateKey(date) : '';
            const isSelected = date && selectedDays.has(dateKey);
            const isToday = date && dateKey === todayKey;
            const hasSlots = date && dayTimeSlots[dateKey] && dayTimeSlots[dateKey].length > 0;

            return (
              <div
                key={index}
                className={`calendar-day ${!date ? 'empty' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${hasSlots ? 'has-appointments' : ''}`}
                onClick={() => toggleDaySelection(date)}
              >
                {date && (
                  <>
                    <span className="day-number">{date.getDate()}</span>
                    {hasSlots && (
                      <div className="appointment-indicator">
                        <span className="indicator-dot"></span>
                        <span className="indicator-count">{dayTimeSlots[dateKey].length}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selectedDays.size > 0 && (
        <div className="selected-days-panel">
          <div className="panel-header">
            <h4>المواعيد المحددة ({selectedDays.size} يوم)</h4>
            <div className="global-reason-section">
              <input
                type="text"
                className="form-control global-reason-input"
                placeholder="السبب (سيطبق على جميع المواعيد)"
                value={globalReason}
                onChange={(e) => setGlobalReason(e.target.value)}
              />
              <button 
                className="btn btn-sm btn-primary apply-reason-btn"
                onClick={applyReasonToAll}
                disabled={!globalReason}
              >
                تطبيق على الكل
              </button>
            </div>
          </div>

          <div className="selected-days-list">
            {Array.from(selectedDays).sort().map(dateKey => {
              const slots = dayTimeSlots[dateKey] || [];
              const dateObj = new Date(dateKey + 'T00:00:00');
              const dayName = weekDays[dateObj.getDay()];
              const dayNumber = dateObj.getDate();
              const monthName = monthNames[dateObj.getMonth()];

              return (
                <div key={dateKey} className="selected-day-card">
                  <div className="day-card-header">
                    <div className="day-info">
                      <span className="day-name">{dayName}</span>
                      <span className="day-date">{dayNumber} {monthName}</span>
                    </div>
                    <button
                      className="remove-day-btn"
                      onClick={() => toggleDaySelection(dateObj)}
                      title="إزالة اليوم"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>

                  <div className="time-slots-container">
                    {slots.map((slot, slotIndex) => (
                      <div key={slotIndex} className="time-slot-row">
                        <div className="time-inputs">
                          <div className="time-input-group">
                            <label>وقت البداية:</label>
                            <input
                              type="time"
                              className="form-control"
                              value={slot.start}
                              onChange={(e) => updateTimeSlot(dateKey, slotIndex, 'start', e.target.value)}
                            />
                          </div>
                          <div className="time-input-group">
                            <label>وقت النهاية:</label>
                            <input
                              type="time"
                              className="form-control"
                              value={slot.end}
                              onChange={(e) => updateTimeSlot(dateKey, slotIndex, 'end', e.target.value)}
                            />
                          </div>
                          <div className="reason-input-group">
                            <label>السبب:</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="مثال: عطلة، سفر، طوارئ...."
                              value={slot.reason}
                              onChange={(e) => updateTimeSlot(dateKey, slotIndex, 'reason', e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="time-slot-actions">
                          {slots.length > 1 && (
                            <button
                              className="remove-slot-btn"
                              onClick={() => removeTimeSlot(dateKey, slotIndex)}
                              title="حذف الوقت"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                          {slotIndex === slots.length - 1 && (
                            <button
                              className="add-slot-btn"
                              onClick={() => addTimeSlot(dateKey)}
                              title="إضافة وقت آخر"
                            >
                              <i className="fas fa-plus"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExceptionalAppointmentsCalendar;
